import { ApiError, isFallbackErrorMessage } from '@/api/http';

/**
 * A send refused by the per-mailbox send rate (#83, `mail.send_rate_per_hour`):
 * the server's `429 rate_limited`, with `Retry-After`.
 *
 * Not the only 429 a send can meet in principle, but the only one the send
 * routes answer, and every reading below holds for any of them: the message
 * did not go, and it can go later.
 */
export function isSendRateLimited(error: unknown): error is ApiError {
  return error instanceof ApiError && error.isRateLimited;
}

/** "about 12 minutes": a wait in words, rounded up to the minute. */
export function waitInWords(seconds: number | undefined): string | undefined {
  if (seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) return undefined;
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes === 1) return 'about a minute';
  if (minutes < 60) return `about ${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  return hours === 1 ? 'about an hour' : `about ${hours} hours`;
}

/**
 * Why a send from the composer did not go, in words the composer shows.
 *
 * Over the send rate the server's own sentence is used when it has one -- it
 * names the mailbox, the limit and the wait -- and the message stays in the
 * composer either way, so the sentence says so rather than leaving the user to
 * wonder whether it went.
 */
export function sendProblem(error: unknown): string {
  if (isSendRateLimited(error)) {
    const wait = waitInWords(error.retryAfter);
    const said = error.message.trim();
    const why =
      said !== '' && !isFallbackErrorMessage(said)
        ? said
        : `Not sent: this mailbox has reached its hourly sending limit.${wait ? ` You can send again in ${wait}.` : ''}`;
    return `${why} Your message is still here.`;
  }
  if (error instanceof ApiError && error.isServiceUnavailable) {
    return 'Sending is temporarily unavailable. You are still signed in — try again in a moment.';
  }
  return error instanceof Error ? error.message : String(error);
}

/**
 * The same refusal as the Outbox shows it: the message is kept and sent by
 * itself once the hour is up, so it says that instead of "send again".
 */
export function outboxRateLimitedText(error: ApiError): string {
  const wait = waitInWords(error.retryAfter);
  return (
    'This mailbox has reached its hourly sending limit, so this message is waiting. ' +
    (wait ? `It will be sent automatically in ${wait}.` : 'It will be sent automatically when the limit lifts.')
  );
}
